import type { ReactNode } from 'react';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from 'baseui';
import { toaster } from 'baseui/toast';
import { useAtomValue, useSetAtom } from 'jotai';
import { ANNOTATION_EVENT_NAMES } from '../../../../analytics/event-name';
import useAnalytics from '../../../../analytics/useAnalytics';
import { ACTION_BY, LABELLING_TYPE } from '../../../../common/constant/constant';
import type { AnnotationLabelT } from '../../../../common/types/types';
import { getRandomId } from '../../../../common/utils/utils';
import { dispatchAtom } from '../../../labelling-v2/atoms/label-store-dispatch/dispatch';
import { EVENTS, UNDOABLE_EVENTS } from '../../../labelling-v2/atoms/label-store-dispatch/events';
import { isLoadingAtom } from '../../../labelling-v2/atoms/tool-atoms';
import useShortcutKeysHandlers from '../../../labelling-v2/components/keyboard-shortcuts/hooks/use-shortcut-keys-handlers';
import { PERFORMANCE_METRIC_EVENTS } from '../../../labelling-v2/constants/performance';
import { useMeasurePaintLatency } from '../../../labelling-v2/hooks/performance/use-measure-paint-latency';
import type { AddAnnotationPayloadT, DeleteAnnotationPayloadT, UpdateAnnotationPayloadT } from '../../analytics/types';
import type { VERTEX_TYPE } from '../../components/annotations/shape/vertex';
import { getVertices } from '../../components/snapping/utils';
import { StageElements } from '../../components/stage-elements/stage-elements';
import BoundingBoxPanel from '../../components/toolbar/bounding-box-panel';
import { shouldEnableMergeSubtractForRectangleOnlyLabelWithoutPolygonTool } from '../../components/toolbar/merge-subtract-polygon-eligibility';
import { MultiSelectPanel } from '../../components/toolbar/mutli-select-panel';
import PolygonPanel from '../../components/toolbar/polygon-panel';
import SegmentationPanel from '../../components/toolbar/segmentation-panel';
import SkeletonPanel from '../../components/toolbar/skeleton-panel';
import { ANNOTATION_TYPE } from '../../constants';
import { STAGE_EVENTS, TOOL } from '../../constants';
import { CSS_VARIABLE_HOVERED_STAGE_STROKE_WIDTH, CSS_VARIABLE_STAGE_ROTATION, CSS_VARIABLE_STAGE_SCALE, CSS_VARIABLE_STAGE_STROKE_WIDTH, CSS_VARIABLE_STAGE_VISIBLE_SCALE } from '../../constants/stage-css-variables';
import type { MetaDataT, StageElementT } from '../../types';
import { type AnnotationT, type ToolT } from '../../types';
import type { BulkAnnotationT, RectDataT, YoloErrorT } from '../../types/annotations.type';
import { createKeyFrame } from '../../utils';
import { type DragEventT, type EventTypeT, DRAG_STATE, STAGE_HOTKEYS } from '../constants';
import useShortcut from '../hooks/use-shortcut';
import { useStageElement } from '../hooks/use-stage-elements';
import getMousePos from '../utils/getMousePos';
import { handleShortcutRegistrationError } from '../utils/shortcut';
import { useFitToScreen } from './hooks/use-fit-to-screen';
import { useSetInitialScaleOnLoad } from './hooks/use-set-initial-scale-on-load';
import { useStageSettings } from './hooks/use-stage-settings';
import useZoomAndPan from './hooks/use-zoom-and-pan';
import { DEFAULT_STAGE_SETTINGS } from './constants';
import { type StageDragHelperTextRefT, StageDragHelperText } from './stage-drag-helper-text';
import type { OnChangeStageSettings, OnCommitStageSettings, StageAnnotation, StageSettingsT } from './types';
const Container = styled('div', ({
  $theme
}) => ({
  backgroundColor: $theme.name.startsWith('light') ? '#F3F3F3' : '#1F1F1F',
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  position: 'relative'
}));
const Stage = styled('div', () => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  display: 'inline-block'
}));
const StageActions = styled('div', () => ({
  position: 'absolute'
}));
const StageOverlay = styled('div', {});
export type ImageSettingsT = {
  brightness: number;
  contrast: number;
};
export type MetadataT = {
  actionDuration: string;
  vertex?: number;
  actionBy?: (typeof ACTION_BY)[keyof typeof ACTION_BY];
};
type VertexType = (typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE];
export type MultiSelectBoxChange = {
  changeX: number | null | undefined;
  changeY: number | null | undefined;
  changeWidth: number | null | undefined;
  changeHeight: number | null | undefined;
  selectedMultiBoxVertexType: VertexType | null | undefined;
};
type StageContext = {
  stage: EventTarget | undefined | null;
  onStage: boolean;
  multiSelectBox: RectDataT | null | undefined;
  setMultiSelectBox: (RectDataT: RectDataT | null) => void;
  referenceMultiSelectBox: RectDataT | null | undefined;
  setReferenceMultiSelectBox: (RectDataT: RectDataT | null) => void;
  selectedMultiBoxVertexType: VertexType | null | undefined;
  setSelectedMultiBoxVertexType: (VertexType) => void;
  addAnnotation: (annotation: AnnotationT, metadata?: MetadataT, copiedAnnotationId?: string | null) => string;
  bulkAddAnnotations: (annotations?: BulkAnnotationT[]) => void | Error;
  updateAnnotation: (annotationIdx: string, annotation: AnnotationT, metadata?: MetadataT) => void;
  deleteAnnotation: (annotationIdx: string, metadata?: MetadataT) => void;
  deleteSelectedAnnotations: () => void;
  mergePolygon: () => void;
  subtractPolygon: () => void;
  getStageDimension: () => [number, number];
  setFileDimension: (width: number, height: number) => void;
  /**
   * @deprecated Use stageSettingsAtom instead
   */
  getStageSettings: () => StageSettingsT;
  /**
   * @deprecated Use stageSettingsAtom instead
   */
  setStageSettings: (stageSettings: Partial<StageSettingsT>) => void;
  fitToScreen: () => void;
  addInterceptor: (event: EventTypeT, interceptor: (stageEvent: StagePointerEventT) => StagePointerEventT) => void;
  removeInterceptor: (event: EventTypeT, interceptor: (stageEvent: StagePointerEventT) => StagePointerEventT) => void;
  getStageCenter: () => [number, number];
  removeElement: (elementName: string) => void;
  renderElement: (newElement: StageElementT) => void;
  multiSelectBoxChange: MultiSelectBoxChange | null | undefined;
};
export const StageContext = createContext<StageContext>({
  stage: null,
  onStage: true,
  addAnnotation: () => '',
  bulkAddAnnotations: () => {},
  updateAnnotation: () => {},
  deleteAnnotation: () => {},
  deleteSelectedAnnotations: () => {},
  mergePolygon: () => {},
  subtractPolygon: () => {},
  setFileDimension: () => {},
  getStageDimension: () => [0, 0],
  getStageSettings: () => DEFAULT_STAGE_SETTINGS,
  setStageSettings: () => {},
  removeElement: () => {},
  renderElement: () => {},
  fitToScreen: () => {},
  addInterceptor: () => {},
  removeInterceptor: () => {},
  getStageCenter: () => ([0, 0] as [number, number]),
  multiSelectBox: undefined,
  setMultiSelectBox: function (): void {},
  referenceMultiSelectBox: undefined,
  setReferenceMultiSelectBox: function () {},
  selectedMultiBoxVertexType: undefined,
  setSelectedMultiBoxVertexType: function () {},
  multiSelectBoxChange: undefined
});
export type MousePosT = [number, number];
type StageEventDataT = {
  mousePos: MousePosT;
  scale: number;
  shiftKey: boolean;
  mouseLeftDown: boolean;
  deltaX: number;
  deltaY: number;
  dragTarget?: Element;
};
export type StagePointerEventT = Omit<PointerEvent, 'detail'> & {
  detail: StageEventDataT;
};

// allowing only sync interceptors to be added, as of now flow is not able to detect the error if we are adding any async interceptor to the interceptor store by calling the addInterceptor function, which should be fixed while migrating to TypeScript.
type InterceptorStoreT = { [k in EventTypeT]?: ((stageEvent: StagePointerEventT) => StagePointerEventT)[] };
export type StageAnnotationConsensus = StageAnnotation & {
  metadata?: {
    consensusInfo?: {
      annotator?: number;
    };
  };
};
export type StageFrameData<Annotation extends StageAnnotation> = {
  annotationStore: {
    [annotationId: string]: Annotation;
  };
  annotationMLStore?: {
    [annotationId: string]: Annotation;
  };
  annotationConsensusStore?: {
    [annotationId: string]: StageAnnotationConsensus;
  };
  imageUrl: string;
};
type PropsT<Annotation extends StageAnnotation> = {
  children: ReactNode;
  frameState: {
    annotationStore: {
      [annotationId: string]: Annotation;
    };
    currentFrame: number;
    frameCount: number;
  };
  showPredefinedSAMToolSizes?: boolean;
  // TODO: Make type enum
  onMount?: (fn: () => void) => void;
  stageSettings: StageSettingsT;
  setLocalStageSettings: React.Dispatch<React.SetStateAction<StageSettingsT>>;
  file: {
    type: string;
    metadata: MetaDataT;
    dataUrl: string | undefined;
  };
  toolState: {
    selectedAnnotationIds: Array<string>;
    selectedAnnotationLabel: AnnotationLabelT | undefined;
    selectedTool: ToolT;
    focusedAnnotationId?: string;
    copiedAnnotationId: string | null;
  };
  readOnly?: boolean;
  isReviewMode?: boolean;
  getGroupingBoxDimensions: (annotation: StageAnnotation[]) => RectDataT;
  getYoloData?: () => {
    bulkAnnotations: BulkAnnotationT[] | null;
    errors: Array<YoloErrorT>;
  } | null;
  renderOverlay?: (container?: HTMLDivElement | null) => ReactNode;
};
export function StageProvider<Annotation extends StageAnnotation>({
  children,
  readOnly = false,
  isReviewMode = false,
  showPredefinedSAMToolSizes,
  onMount,
  stageSettings: stageSettingsProp,
  setLocalStageSettings: setStageSettingsProp,
  frameState,
  toolState,
  file,
  getGroupingBoxDimensions,
  getYoloData,
  renderOverlay
}: PropsT<Annotation>) {
  const [editorDimensions, setEditorDimensions] = useState(([(window.innerWidth as number), (window.innerHeight as number)] as [number, number]));
  const isLoading = useAtomValue(isLoadingAtom);
  const [multiSelectBox, setMultiSelectBox] = useState<RectDataT | null>(null);
  const [referenceMultiSelectBox, setReferenceMultiSelectBox] = useState<RectDataT | null>(null);
  const [onStage, setOnStage] = useState(true);
  const [selectedMultiBoxVertexType, setSelectedMultiBoxVertexType] = useState<(typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE] | undefined | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const stageActionsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const containerCallbackRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    setContainerEl(el);
  }, []);
  const stageDragHelperTextRef = useRef<StageDragHelperTextRefT>(null);
  const stageEventTargetRef = useRef<EventTarget>(new EventTarget());
  const stageBoundingRectRef = useRef<ClientRect | undefined | null>(null);
  const interceptorStore = useRef<InterceptorStoreT>({});
  const aspectRatioRef = useRef<number | undefined | null>(null);
  const dragEvent = useRef<DragEventT | null>(null);
  const isPointerDown = useRef<boolean>(false);
  const lastMousePositionRef = useRef<StagePointerEventT | undefined>();
  const numSelectedAnnotations = toolState.selectedAnnotationIds.length;
  const {
    elements,
    removeElement,
    addElement
  } = useStageElement();
  const measurePaintLatency = useMeasurePaintLatency({
    throttleMs: 500,
    minDurationMs: 16
  });
  const {
    stageSettingsRef,
    onChangeStageSettings,
    onCommitStageSettings
  } = useStageSettings({
    stageSettings: stageSettingsProp,
    setStageSettings: setStageSettingsProp
  });
  useEffect(() => {
    if (numSelectedAnnotations >= 0) {
      setReferenceMultiSelectBox(null);
    }
  }, [numSelectedAnnotations]);
  const isVideoMode = file.type !== LABELLING_TYPE.PDF && frameState.frameCount > 1;
  const annotationStore = frameState.annotationStore;
  useEffect(() => {
    let newMultiSelectRect: RectDataT | null = null;
    if (toolState.selectedAnnotationIds.length > 1) {
      const dimensionData = toolState.selectedAnnotationIds.map(id => annotationStore[id]).filter((annotation): annotation is Annotation => annotation !== undefined);
      newMultiSelectRect = getGroupingBoxDimensions(dimensionData);
    }
    setMultiSelectBox(newMultiSelectRect);
  }, [annotationStore, getGroupingBoxDimensions, toolState.selectedAnnotationIds]);
  const {
    width,
    height
  } = file.metadata;
  const isStageDraggable = useMemo(() => {
    return toolState.selectedTool === TOOL.POINTER && numSelectedAnnotations === 0;
  }, [toolState.selectedTool, numSelectedAnnotations]);
  const isDrawing = useMemo(() => {
    return toolState.selectedAnnotationLabel !== null && toolState.selectedTool !== TOOL.POINTER;
  }, [toolState.selectedAnnotationLabel, toolState.selectedTool]);
  const shouldDisableStageDraggingEvents = useCallback((e: PointerEvent) => {
    if (e.altKey) {
      return true;
    }
    return false;
  }, []);
  const applyStageSettingsToDOMNodes = useCallback(() => {
    const settings = stageSettingsRef.current;
    if (!settings) {
      return;
    }
    const {
      scale,
      rotation,
      x,
      y
    } = settings;
    const matrix = `matrix(${scale}, 0 , 0, ${scale}, ${x}, ${y}) rotate(${rotation}deg)`;
    if (stageRef.current) {
      stageRef.current.style.setProperty('transform', matrix);
      // Below css variables are added so that child nodes doesn't need to
      // re-render on scale / rotation change, child nodes should consume these
      // CSS variables
      stageRef.current.style.setProperty(CSS_VARIABLE_STAGE_SCALE, scale.toString());
      stageRef.current.style.setProperty(CSS_VARIABLE_STAGE_ROTATION, `${rotation}deg`);
      stageRef.current.style.setProperty(CSS_VARIABLE_STAGE_VISIBLE_SCALE, (1 / scale).toString());
      stageRef.current.style.setProperty(CSS_VARIABLE_STAGE_STROKE_WIDTH, `${2 / scale}px`);
      stageRef.current.style.setProperty(CSS_VARIABLE_HOVERED_STAGE_STROKE_WIDTH, `${3 / scale}px`);
    }
    if (stageActionsRef.current) {
      stageActionsRef.current.style.setProperty('transform', matrix);
      // Below css variables are added so that child nodes doesn't need to
      // re-render on scale / rotation change, child nodes should consume these
      // CSS variables
      stageActionsRef.current.style.setProperty(CSS_VARIABLE_STAGE_STROKE_WIDTH, `${2 / scale}px`);
      stageActionsRef.current.style.setProperty(CSS_VARIABLE_STAGE_VISIBLE_SCALE, (1 / scale).toString());
      stageActionsRef.current.style.setProperty(CSS_VARIABLE_STAGE_ROTATION, `${rotation}deg`);
    }
  }, [stageSettingsRef]);
  const handleChangeStageSettingsAndApplyToDOMNodes = useCallback<OnChangeStageSettings>(stageSettings => {
    onChangeStageSettings(stageSettings);
    applyStageSettingsToDOMNodes();
  }, [onChangeStageSettings, applyStageSettingsToDOMNodes]);
  const handleCommitStageSettingsAndApplyToDOMNodes = useCallback<OnCommitStageSettings>(stageSettings => {
    onCommitStageSettings(stageSettings);
    applyStageSettingsToDOMNodes();
  }, [onCommitStageSettings, applyStageSettingsToDOMNodes]);
  const handleShowStageDragHelperText = () => {
    stageDragHelperTextRef.current?.show();
  };
  useSetInitialScaleOnLoad({
    containerRef,
    stageRef,
    isImageLoaded: !!width && !!height,
    onCommitStageSettingsAndApplyToDOMNodes: handleCommitStageSettingsAndApplyToDOMNodes
  });
  const fitToScreen = useFitToScreen({
    stageRef,
    containerRef,
    onCommitStageSettingsAndApplyToDOMNodes: handleCommitStageSettingsAndApplyToDOMNodes
  });
  useZoomAndPan({
    containerRef: containerRef,
    stageRef: stageRef,
    isDraggable: isStageDraggable,
    isDrawing: isDrawing,
    dragEventRef: dragEvent,
    onChangeStageSettingsAndApplyToDOMNodes: handleChangeStageSettingsAndApplyToDOMNodes,
    onCommitStageSettingsAndApplyToDOMNodes: handleCommitStageSettingsAndApplyToDOMNodes,
    onShowStageDragHelperText: handleShowStageDragHelperText
  });
  useEffect(() => {
    onMount?.(fitToScreen);
  }, [fitToScreen, onMount]);
  const stageTarget = stageEventTargetRef.current;
  const {
    sendEvent
  } = useAnalytics();
  const dispatch = useSetAtom(dispatchAtom);
  const getCurrentFrame = useCallback(() => {
    return frameState.currentFrame;
  }, [frameState.currentFrame]);
  const getStageSettings = useCallback(() => stageSettingsRef.current || DEFAULT_STAGE_SETTINGS, [stageSettingsRef]);
  const setFileDimension = useCallback((width: number, height: number) => {
    aspectRatioRef.current = width / height;
    dispatch({
      type: EVENTS.SET_IMAGE_META_DATA,
      metaData: {
        width,
        height
      }
    });
    setEditorDimensions([width, height]);
  }, [dispatch]);
  const getStageDimension = useCallback(() => {
    return editorDimensions;
  }, [editorDimensions]);

  // Create a reference to store the mapping of label titles to annotation IDs for segmentation
  const segmentationLabelToAnnotationId = useRef<{
    [labelTitle: string]: string;
  }>({});
  const addAnnotation = useCallback((annotation: Annotation, metaData?: MetadataT) => {
    const copiedAnnotationId = toolState.copiedAnnotationId;
    const annotationData = {
      ...annotation,
      locked: false
    };
    if (isVideoMode) {
      annotationData.keyFrame = createKeyFrame(true);
    }
    const currentFrame = getCurrentFrame();
    let annotationId = '';
    if (annotation.annotationType === ANNOTATION_TYPE.SEGMENTATION) {
      // Reuse existing annotation ID if available, otherwise create a new one
      if (segmentationLabelToAnnotationId.current[annotation.annotationLabel.title]) {
        // @ts-expect-error todo(ts-migration) TS2322 Type 'string | undefined' is not assignable to type 'string'.
        annotationId = segmentationLabelToAnnotationId.current[annotation.annotationLabel.title];
      } else {
        annotationId = copiedAnnotationId ?? getRandomId();
        segmentationLabelToAnnotationId.current[annotation.annotationLabel.title] = annotationId;
      }
    } else {
      annotationId = copiedAnnotationId ?? getRandomId();
    }
    dispatch({
      type: UNDOABLE_EVENTS.ADD_ANNOTATION,
      frameIndex: currentFrame,
      annotationIdx: annotationId,
      annotationData
    });
    const numberOfVertices = getVertices(annotation).length;
    sendEvent({
      action: ANNOTATION_EVENT_NAMES.ADD_ANNOTATION,
      payload: ({
        objectType: annotation.annotationType,
        label: toolState.selectedAnnotationLabel?.title,
        objectId: annotationId,
        timeTaken: metaData?.actionDuration,
        frame: isVideoMode ? currentFrame + 1 : undefined,
        // +1 syncing with the UI,
        actionBy: ACTION_BY.POINTER,
        numberOfVertices,
        annotation_type: 'manual'
      } as AddAnnotationPayloadT)
    });
    return annotationId;
  }, [getCurrentFrame, isVideoMode, dispatch, sendEvent, toolState.selectedAnnotationLabel?.title, toolState.copiedAnnotationId]);
  const updateAnnotation = useCallback((annotationId: string, annotation: Annotation, metadata?: MetadataT) => {
    const annotationData = {
      ...annotation
    };
    if (isVideoMode) {
      annotationData.keyFrame = createKeyFrame(true);
    }
    const currentFrameIdx = getCurrentFrame();
    dispatch({
      type: UNDOABLE_EVENTS.UPDATE_ANNOTATION,
      frameIndex: currentFrameIdx,
      annotationIdx: annotationId,
      annotationData
    });
    const numberOfVertices = getVertices(annotation).length;
    sendEvent({
      action: ANNOTATION_EVENT_NAMES.UPDATE_ANNOTATION,
      payload: ({
        objectType: annotation.annotationType,
        label: annotation.annotationLabel.title,
        objectId: annotationId,
        timeTaken: metadata?.actionDuration,
        actionBy: ACTION_BY.POINTER,
        frame: isVideoMode ? frameState.currentFrame + 1 : undefined,
        // +1 syncing with the UI,
        numberOfVertices,
        annotation_type: annotation.keyFrame?.type === 'AUTOMATED' ? 'ml' : 'manual'
      } as UpdateAnnotationPayloadT)
    });
  }, [isVideoMode, getCurrentFrame, dispatch, sendEvent, frameState.currentFrame]);
  const deleteAnnotation = useCallback((annotationIdx: string, metadata?: MetadataT) => {
    const numberOfVertices = getVertices(
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    frameState.annotationStore[annotationIdx]).length;
    dispatch({
      type: UNDOABLE_EVENTS.DELETE_ANNOTATION,
      frameIndex: frameState.currentFrame,
      annotationIdx
    });
    sendEvent({
      action: ANNOTATION_EVENT_NAMES.DELETE_ANNOTATION,
      payload: ({
        objectType:
        // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
        frameState.annotationStore[annotationIdx].annotationType,
        label: toolState.selectedAnnotationLabel?.title,
        objectId: annotationIdx,
        timeTaken: metadata?.actionDuration,
        actionBy: metadata?.actionBy ? metadata.actionBy : ACTION_BY.POINTER,
        frame: frameState.currentFrame + 1,
        // +1 to match the UI index
        numberOfVertices,
        annotation_type: frameState.annotationStore[annotationIdx]?.keyFrame?.type === 'AUTOMATED' ? 'ml' : 'manual'
      } as DeleteAnnotationPayloadT)
    });
  }, [frameState.currentFrame, frameState.annotationStore, dispatch, sendEvent, toolState.selectedAnnotationLabel?.title]);
  const mergePolygon = useCallback(() => {
    dispatch({
      type: UNDOABLE_EVENTS.MERGE_POLYGON
    });
  }, [dispatch]);
  const subtractPolygon = useCallback(() => {
    dispatch({
      type: UNDOABLE_EVENTS.SUBTRACT_POLYGON
    });
  }, [dispatch]);
  const deleteSelectedAnnotations = useCallback(() => {
    dispatch({
      type: UNDOABLE_EVENTS.DELETE_SELECTED_ANNOTATIONS
    });
  }, [dispatch]);
  const bulkAddAnnotations = useCallback((annotations?: BulkAnnotationT[]) => {
    if (annotations) {
      dispatch({
        type: UNDOABLE_EVENTS.BULK_ADD_ANNOTATIONS,
        annotations
      });
      return;
    }
    const yoloData = getYoloData?.();
    if (yoloData?.bulkAnnotations) {
      dispatch({
        type: UNDOABLE_EVENTS.BULK_ADD_ANNOTATIONS,
        annotations: yoloData.bulkAnnotations
      });
    } else {
      return new Error('No YOLO data is available for this task.');
    }
    if (yoloData?.errors && yoloData.errors.length > 0) {
      yoloData.errors.map(error => {
        toaster.negative(error.message);
      });
    }
  }, [dispatch, getYoloData]);
  const createStageEvent = useCallback((e: PointerEvent, event: EventTypeT): StagePointerEventT => {
    const stageSettings = getStageSettings();
    const stageMousePos = getMousePos(e, stageBoundingRectRef.current, stageSettings.scale, stageSettings.rotation);
    const eventDetailData: StageEventDataT = {
      mousePos: stageMousePos,
      // Spreading e doesn't copy all the properties
      shiftKey: e.shiftKey,
      scale: stageSettings.scale,
      mouseLeftDown: e.buttons === 1,
      deltaX: 0,
      deltaY: 0,
      dragTarget: (dragEvent.current?.target as Element) ?? undefined
    };
    if (dragEvent.current) {
      eventDetailData.deltaX = eventDetailData.mousePos[0] - dragEvent.current.dragStartMousePos[0];
      eventDetailData.deltaY = eventDetailData.mousePos[1] - dragEvent.current.dragStartMousePos[1];
    }
    const stageEvent = {
      ...e,
      detail: eventDetailData
    };
    return (interceptorStore.current[event] || []).reduce<StagePointerEventT>((updatedStageEvent, interceptor) => {
      return interceptor(updatedStageEvent);
    }, stageEvent);
  }, [interceptorStore, getStageSettings]);
  const isDraggingAllowed = (e: PointerEvent) => {
    /*
      dragging is allowed when selected tool is pointer or trying to drag
      last drawn annotation's vertex.
       */
    if (toolState.selectedTool === TOOL.POINTER || (e.target as HTMLElement).getAttribute('data-stage-draggable')) {
      return true;
    }
    return false;
  };

  /**
   * Allows children to block stage event handler for some specific use cases.
   */
  const isCustomMouseHandler = (e: PointerEvent) => {
    return (e.target as HTMLElement).getAttribute('data-stage-custom');
  };
  function onPointerMove(e: PointerEvent) {
    if (shouldDisableStageDraggingEvents(e)) {
      return;
    }
    if (dragEvent.current) {
      if (dragEvent.current.dragState === DRAG_STATE.DRAG_START) {
        stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.DRAG_START, createStageEvent(e, STAGE_EVENTS.DRAG_START)));
      }
      dragEvent.current.dragState = DRAG_STATE.DRAG;
      measurePaintLatency(PERFORMANCE_METRIC_EVENTS.DRAG_TO_PAINT, () => {
        stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.DRAG, createStageEvent(e, STAGE_EVENTS.DRAG)));
      });
      return;
    }
    const mouseMoveEvent = createStageEvent(e, STAGE_EVENTS.POINTER_MOVE);
    lastMousePositionRef.current = mouseMoveEvent;
    stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.POINTER_MOVE, mouseMoveEvent));
  }
  function onPointerDown(e: PointerEvent) {
    if (isLoading) return;
    if (shouldDisableStageDraggingEvents(e)) {
      return;
    }

    // Only allow left click to trigger events
    if (e.button !== 0) return;

    // Skip event if drag was happening and mouseup was done outside stage
    if (dragEvent.current !== null) return;
    const stageSettings = getStageSettings();
    if (isCustomMouseHandler(e)) {
      const mousePos = getMousePos(e, stageBoundingRectRef.current, stageSettings.scale, stageSettings.rotation);

      /**
       * setting dragEvent, emitting DRAG_START only incase of pointerMove event after this
       * and blocking POINTER_DOWN event here (because target clicked is not stage).
       */
      dragEvent.current = {
        dragState: DRAG_STATE.DRAG_START,
        dragStartMousePos: mousePos,
        target: e.target
      };
      return;
    }
    if (isDraggingAllowed(e)) {
      // @ts-expect-error todo(ts-migration) TS2339 Property 'tagName' does not exist on type 'EventTarget'.
      const clickedOnStage = e.target.tagName === 'svg' && e.target.getAttribute('data-stage-svg');

      // Drag element if click not on stage
      if (!clickedOnStage) {
        const mousePos = getMousePos(e, stageBoundingRectRef.current, stageSettings.scale, stageSettings.rotation);

        /**
         * setting dragEvent, emitting DRAG_START only incase of pointerMove event after this
         * and blocking POINTER_DOWN event here (because target clicked is not stage).
         */
        dragEvent.current = {
          dragState: DRAG_STATE.DRAG_START,
          dragStartMousePos: mousePos,
          target: e.target
        };
      }
      return;
    }
    //Skip event if pointerdown is already there
    if (isPointerDown.current) {
      return;
    }
    isPointerDown.current = true;
    stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.POINTER_DOWN, createStageEvent(e, STAGE_EVENTS.POINTER_DOWN)));
  }
  const onPointerUp = useCallback((e: PointerEvent) => {
    if (isLoading) return;
    if (shouldDisableStageDraggingEvents(e)) {
      return;
    }
    if (dragEvent.current) {
      //sending DRAG_END event only if DRAG has happened.
      if (dragEvent.current.dragState === DRAG_STATE.DRAG) {
        stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.DRAG_END, createStageEvent(e, STAGE_EVENTS.DRAG_END)));
      }
      dragEvent.current = null;
      return;
    }
    stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.POINTER_UP, createStageEvent(e, STAGE_EVENTS.POINTER_UP)));
    isPointerDown.current = false;
  }, [isLoading, shouldDisableStageDraggingEvents, createStageEvent, stageTarget]);
  const onPointerEnter = useCallback((e: PointerEvent) => {
    if (shouldDisableStageDraggingEvents(e)) {
      return;
    }
    stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.POINTER_ENTER, createStageEvent(e, STAGE_EVENTS.POINTER_ENTER)));
    setOnStage(true);
  }, [shouldDisableStageDraggingEvents, createStageEvent, stageTarget]);
  const onPointerLeave = useCallback((e: PointerEvent) => {
    if (shouldDisableStageDraggingEvents(e)) {
      return;
    }
    stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.POINTER_LEAVE, createStageEvent(e, STAGE_EVENTS.POINTER_LEAVE)));
    setOnStage(false);
  }, [shouldDisableStageDraggingEvents, createStageEvent, stageTarget]);
  const onRightClick = (e: PointerEvent) => {
    stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.RIGHT_CLICK, createStageEvent(e, STAGE_EVENTS.RIGHT_CLICK)));
    e.preventDefault();
    e.stopPropagation();
  };
  const keyPressHandlers = useShortcutKeysHandlers({
    updateAnnotation,
    selectedAnnotationIds: toolState.selectedAnnotationIds,
    annotationStore: annotationStore,
    stageDimensions: editorDimensions
  });
  const onKeyPress = useCallback((event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowLeft':
        keyPressHandlers.onArrowLeftPress();
        break;
      case 'ArrowRight':
        keyPressHandlers.onArrowRightPress();
        break;
      case 'ArrowUp':
        keyPressHandlers.onArrowUpPress();
        break;
      case 'ArrowDown':
        keyPressHandlers.onArrowDownPress();
        break;
      default:
        break;
    }
  }, [keyPressHandlers]);
  const {
    registerHotKeys
  } = useShortcut();
  useEffect(() => {
    if (isReviewMode) {
      // in review mode arrow keys are used to navigate through annotations in review side panel list view
      return;
    }
    const hotKeys = Object.keys(STAGE_HOTKEYS).map(key => ({
      keys: [key],
      onAction: onKeyPress
    }));
    const {
      errors,
      removeHotKeys
    } = registerHotKeys(hotKeys);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [onKeyPress, registerHotKeys, isReviewMode]);
  function onClick(e: PointerEvent) {
    if (e.detail === 2) {
      // Double click event is triggered if the click event is triggered with detail as 2
      stageTarget.dispatchEvent(new CustomEvent(STAGE_EVENTS.DOUBLE_CLICK, createStageEvent(e, STAGE_EVENTS.DOUBLE_CLICK)));
    }
  }
  const getStageCenter = useCallback(() => {
    return ([editorDimensions[0] / 2, editorDimensions[1] / 2] as [number, number]);
  }, [editorDimensions]);
  useEffect(() => {
    handleCommitStageSettingsAndApplyToDOMNodes(stageSettingsProp);

    // Update stageBoundingRect so that subsequent stage events can use
    // correct bounding rect for pointer events calculations.
    stageBoundingRectRef.current = stageRef.current?.getBoundingClientRect();
  }, [stageSettingsProp, handleCommitStageSettingsAndApplyToDOMNodes, stageSettingsRef]);

  // add only sync interceptors, do not add async interceptors.
  const addInterceptor = useCallback((event: EventTypeT, interceptor: (stageEvent: StagePointerEventT) => StagePointerEventT) => {
    const interceptors = interceptorStore.current[event];
    if (!interceptors) {
      interceptorStore.current = {
        ...interceptorStore.current,
        [(event as string)]: [interceptor]
      };
      return;
    }
    if (interceptors.find(currentInterceptor => interceptor === currentInterceptor)) {
      return;
    }
    interceptors.push(interceptor);
  }, []);
  const removeInterceptor = useCallback((event: EventTypeT, interceptor: (stageEvent: StagePointerEventT) => StagePointerEventT) => {
    const interceptors = interceptorStore.current[event];
    if (!interceptors) {
      return;
    }
    const idx = interceptors.indexOf(interceptor);
    if (idx !== -1) {
      interceptors.splice(idx, 1);
    }
  }, []);
  const cursorPointerTools: ToolT[] = [TOOL.POINTER, TOOL.SEGMENTATION];
  const segmentationActive = toolState.selectedTool === TOOL.SEGMENTATION;
  const multiSelectBoxChange = multiSelectBox && referenceMultiSelectBox ? {
    changeX: multiSelectBox.x - referenceMultiSelectBox.x || null,
    changeY: multiSelectBox.y - referenceMultiSelectBox.y || null,
    changeWidth: referenceMultiSelectBox.width > 0 ? multiSelectBox.width - referenceMultiSelectBox.width : null,
    changeHeight: referenceMultiSelectBox.height > 0 ? multiSelectBox.height - referenceMultiSelectBox.height : null,
    selectedMultiBoxVertexType
  } : null;
  const shouldShowPolygonPanel = !isVideoMode && toolState.selectedTool === TOOL.POLYGON;
  const hasSkeletonTemplate = !!toolState.selectedAnnotationLabel?.toolConfigs?.[TOOL.SKELETON]?.skeletonTemplates?.length;
  const isSkeletonActive = toolState.selectedTool === TOOL.SKELETON && !hasSkeletonTemplate;
  const isBoundingBoxActive = toolState.selectedTool === TOOL.RECTANGLE;
  const isMultiSelectActive = toolState.selectedTool === TOOL.POINTER && toolState.selectedAnnotationIds.length > 1;
  const multiSelectSelectedAnnotations = toolState.selectedAnnotationIds.map(id => annotationStore[id]).filter((annotation): annotation is Annotation => annotation !== undefined);
  const shouldEnableMultiSelectMergeSubtractPanel = shouldEnableMergeSubtractForRectangleOnlyLabelWithoutPolygonTool(multiSelectSelectedAnnotations);
  const shouldRenderMultiSelectPanel = isMultiSelectActive && shouldEnableMultiSelectMergeSubtractPanel;
  return <Container ref={containerCallbackRef} data-testid="stage-container">
      <StageContext.Provider
    // eslint-disable-next-line react-hooks/refs
    value={{
      // eslint-disable-next-line react-hooks/refs
      stage: stageEventTargetRef.current,
      onStage,
      addAnnotation,
      bulkAddAnnotations,
      updateAnnotation,
      deleteAnnotation,
      deleteSelectedAnnotations,
      mergePolygon,
      subtractPolygon,
      multiSelectBox,
      setMultiSelectBox,
      referenceMultiSelectBox,
      setReferenceMultiSelectBox,
      multiSelectBoxChange,
      selectedMultiBoxVertexType,
      setSelectedMultiBoxVertexType,
      setFileDimension,
      getStageDimension,
      getStageSettings,
      setStageSettings: onCommitStageSettings,
      fitToScreen,
      addInterceptor,
      removeInterceptor,
      getStageCenter,
      removeElement,
      renderElement: addElement
    }}>
        <Stage data-testid="stage" ref={stageRef} style={{
        width: `${editorDimensions[0]}px`,
        height: `${editorDimensions[1]}px`,
        cursor: !cursorPointerTools.includes(toolState.selectedTool) && toolState.selectedAnnotationLabel !== null ? 'crosshair' : 'initial',
        userSelect: 'none',
        // @ts-expect-error todo(ts-migration) TS2322 Type 'number' is not assignable to type 'AspectRatio'.
        // eslint-disable-next-line react-hooks/refs
        aspectRatio: aspectRatioRef.current
      }} {...{
        onPointerDown: onPointerDown,
        onPointerUp: onPointerUp,
        ...(!readOnly && {
          onPointerEnter: onPointerEnter,
          onPointerMove: onPointerMove,
          onPointerLeave: onPointerLeave,
          onContextMenu: onRightClick
        })
      }}
      // @ts-expect-error todo(ts-migration) TS2322 Type '(e: PointerEvent) => void' is not assignable to type 'MouseEventHandler<HTMLDivElement>'.
      onClick={onClick}>
          {children}
        </Stage>
        {renderOverlay && <StageOverlay>
            {renderOverlay(containerEl)}
            <StageDragHelperText ref={stageDragHelperTextRef} />
          </StageOverlay>}
        {segmentationActive && <SegmentationPanel showPredefinedSizes={showPredefinedSAMToolSizes} />}
        {shouldShowPolygonPanel && <PolygonPanel />}
        {isSkeletonActive && <SkeletonPanel />}
        {isBoundingBoxActive && Boolean(getYoloData) && <BoundingBoxPanel />}
        {shouldRenderMultiSelectPanel && <MultiSelectPanel />}
        <StageActions ref={stageActionsRef} style={{
        width: `${editorDimensions[0]}px`,
        height: `${editorDimensions[1]}px`,
        cursor: !cursorPointerTools.includes(toolState.selectedTool) && toolState.selectedAnnotationLabel !== null ? 'crosshair' : 'initial',
        userSelect: 'none',
        // @ts-expect-error todo(ts-migration) TS2322 Type 'number' is not assignable to type 'AspectRatio'.
        // eslint-disable-next-line react-hooks/refs
        aspectRatio: aspectRatioRef.current,
        position: 'absolute',
        pointerEvents: 'none'
      }}>
          <StageElements customElements={elements} />
        </StageActions>
      </StageContext.Provider>
    </Container>;
}